import React, { useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import clsx from 'clsx';
import { HeaderDataItem } from '@interfaces/models/header';
import Link from '@components/atoms/link/link';
import dynamic from 'next/dynamic';
import styles from '../main-navigation.module.scss';
import NewHomepagesEntry from './new-homepages-entry';
const DesktopPopupColumn = dynamic(() => import('@components/molecules/main-nav/components/desktop-popup-column'), {
  ssr: false,
});

interface DesktopItemProps {
  headerDataItem: HeaderDataItem;
  show: boolean;
  onSelect: (id: number | null) => void;
  onClick: (label: string) => void;
  isMainNavigationOpen: boolean;
  setIsMainNavigationOpen: (isOpened: boolean) => void;
  isShoppingPrefsEnabled?: boolean;
}

const DesktopItem: React.FC<DesktopItemProps> = ({
  headerDataItem,
  show,
  onSelect,
  onClick,
  isMainNavigationOpen,
  setIsMainNavigationOpen,
  isShoppingPrefsEnabled = false,
}) => {
  const hasChildren = !!headerDataItem?.content?.length;
  const isLink = headerDataItem?.url?.seo !== '#';
  const [isOpen, setIsOpen] = useState(false);
  const nodeRef = React.useRef(null);
  const headerWrapperRef = useRef<HTMLElement | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debounceCallback = (func: () => void, delay: number) => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(func, delay);
  };

  const handleMouseEnter = () => {
    const openItem = () => {
      onSelect(headerDataItem.id);
      setIsOpen(true);
    };
    if (isMainNavigationOpen) {
      openItem();
      return;
    }
    debounceCallback(() => {
      openItem();
      setIsMainNavigationOpen(true);
    }, 300);
  };

  const handleMouseLeave = () => {
    clearTimeout(timeoutRef.current);

    // If focus is inside the dropdown, move it to the header element before closing
    const dropdownElement = nodeRef.current;
    if (
      dropdownElement &&
      document.activeElement &&
      dropdownElement.contains(document.activeElement) &&
      headerWrapperRef.current
    ) {
      const focusableElement = headerWrapperRef.current.firstElementChild as HTMLElement | null;
      if (focusableElement) {
        // For <span> tags with tabIndex="-1", temporarily make them focusable
        const originalTabIndex = focusableElement.getAttribute('tabindex');
        if (originalTabIndex === '-1') {
          focusableElement.setAttribute('tabindex', '0');
        }

        focusableElement.focus();

        // Restore original tabIndex
        if (originalTabIndex === '-1') {
          focusableElement.setAttribute('tabindex', '-1');
        }
      }
    }

    onSelect(null);
    setIsOpen(false);
  };

  return (
    <li
      key={headerDataItem.id}
      onMouseEnter={handleMouseEnter}
      onFocus={() => onSelect(headerDataItem.id)}
      onMouseLeave={handleMouseLeave}
      onBlur={() => onSelect(null)}
      className={clsx({
        [styles.desktop__noChildren]: !hasChildren,
        [styles['mainNav__item--highlight']]: headerDataItem?.mnemonic === 'sale',
      })}
      data-mnemonic={headerDataItem?.mnemonic}
    >
      <span
        className={styles.desktop__headerWrapper}
        ref={headerWrapperRef}
      >
        {isLink ? (
          <Link
            href={headerDataItem.url.seo}
            tabIndex={0}
            onClick={() => onClick(headerDataItem.localizedName)}
            className={styles.desktop__headerLink}
          >
            {headerDataItem.localizedName}
          </Link>
        ) : (
          <span
            tabIndex={-1}
            className={styles.desktop__headerText}
          >
            {headerDataItem.localizedName}
          </span>
        )}
      </span>
      {/* Popup */}
      {hasChildren && (
        <details
          open={isOpen}
          aria-label={headerDataItem.localizedName}
        >
          <summary>
            <span />
          </summary>
          <CSSTransition
            classNames={{
              appear: styles['fade-enter'],
              appearActive: styles['fade-enter-active'],
              exit: styles['fade-exit'],
              exitActive: styles['fade-exit-active'],
            }}
            nodeRef={nodeRef}
            timeout={300}
            in={show}
            unmountOnExit
            mountOnEnter
          >
            <div
              className={styles.desktop__popup}
              ref={nodeRef}
            >
              <div className="innerContainer">
                {/* Render columns */}
                {isShoppingPrefsEnabled && (
                  <NewHomepagesEntry
                    headerDataItem={headerDataItem}
                    onClick={onClick}
                  />
                )}
                {headerDataItem.content.map((link: HeaderDataItem) => (
                  <DesktopPopupColumn
                    headerDataItem={link}
                    key={link.id}
                    onClick={(label: string) => onClick(`${headerDataItem.localizedName}/${label}`)}
                  />
                ))}
              </div>
            </div>
          </CSSTransition>
        </details>
      )}
    </li>
  );
};

export default DesktopItem;
